import { styled } from '@porsche-code/kado-styled-system/jsx';
import {
  PSegmentedControl,
  PSegmentedControlItem,
  PSelect,
  PSelectOption,
  PSelectProps,
} from '@porsche-design-system/components-react';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { useState } from 'react';
dayjs.extend(customParseFormat);

export type Meridiem = 'AM' | 'PM';

export const get24HFormat = (hour: number, meridiem: Meridiem) =>
  parseInt(dayjs(`${hour % 12} ${meridiem}`, 'h A').format('H'));

export const getAmPm = (hour: number) =>
  dayjs().set('hour', hour).format('A') as Meridiem;

const Layout = styled('div', {
  base: {
    display: 'grid',
    gridTemplateColumns: '160px auto',
    gap: 'md',
    alignItems: 'end',
  },
});

const AmPm = styled('div', {
  base: {
    minWidth: '135px',
  },
});

export const HourSelect = ({
  value,
  isRequired,
  isDisabled,
  is24HourFormat,
  label,
  onChange,
  timeZone,
}: {
  value: number;
  isRequired?: boolean;
  isDisabled?: boolean;
  is24HourFormat: boolean;
  label?: string;
  timeZone?: string;
  onChange: (hour: number) => void;
}) => {
  const [meridiem, setMeridiem] = useState<Meridiem>(getAmPm(value));

  return (
    <Layout>
      <PSelect
        name="hourSelect"
        disabled={isDisabled}
        required={isRequired}
        value={
          typeof value === 'number'
            ? String(value % (is24HourFormat ? 24 : 12))
            : ''
        }
        label={label}
        aria-label={label}
        onChange={(e) => {
          const { value: selectedValue } = e.target as HTMLElement &
            PSelectProps;
          const hour = parseInt(String(selectedValue));
          onChange(is24HourFormat ? hour : get24HFormat(hour, meridiem));
        }}
      >
        {Array.from({
          length: is24HourFormat ? 24 : 12,
        }).map((_, index) => (
          <PSelectOption key={index} value={String(index)}>
            {dayjs()
              .set('hour', index)
              .format(is24HourFormat ? 'HH:00' : 'hh:00')}
            {timeZone ? ` ${timeZone}` : null}
          </PSelectOption>
        ))}
      </PSelect>
      {!is24HourFormat && (
        <AmPm>
          <PSegmentedControl
            columns="2"
            value={meridiem}
            onChange={(event) => {
              const m = event.detail.value as Meridiem;
              setMeridiem(m);
              onChange(get24HFormat(value, m));
            }}
          >
            <PSegmentedControlItem disabled={isDisabled} value="AM">
              AM
            </PSegmentedControlItem>
            <PSegmentedControlItem disabled={isDisabled} value="PM">
              PM
            </PSegmentedControlItem>
          </PSegmentedControl>
        </AmPm>
      )}
    </Layout>
  );
};
